/* Copy dos modais da área Empresas (create/block/unblock/assign-plan) + catálogo de erros.
   Separado de `companies.ts` só por causa do limite de 500 LOC (§4.7) — semanticamente é a
   mesma área; `useCompaniesContent()` funde os dois neste módulo único para quem consome.

   `CompanyErrorKey` usa camelCase (`ownerNotFound`, `planKeyTaken`) em vez de espelhar o
   `ApiErrorCode` snake_case do backend (`owner_not_found`, `plan_key_taken`): a chave vira
   propriedade do objeto `errors` abaixo, e `useNamingConvention` rejeita propriedade snake_case.
   `company-error.ts` faz o mapeamento via `switch` (mesmo idioma de
   `pack-submission-status.ts`). */
import type { Bilingual } from '../bilingual.js';

export type CompanyErrorKey =
  | 'notFound'
  | 'notBlocked'
  | 'ownerNotFound'
  | 'invalidFormat'
  | 'publicBlocked'
  | 'slugTaken'
  | 'invalidLogo'
  | 'logoTooLarge'
  | 'invalidSlug'
  | 'unauthorized'
  | 'planKeyTaken'
  | 'graceExpired'
  | 'network'
  | 'default'
  | 'unexpected';

export interface CompaniesModalsContent {
  readonly reasonDialog: {
    readonly reasonLabel: string;
    readonly reasonPlaceholder: string;
    readonly characterCount: (n: number) => string;
    readonly cancel: string;
  };
  readonly blockModal: {
    readonly title: (name: string) => string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly alreadyBlocked: (name: string) => string;
    readonly partialSuccess: (name: string, steps: string) => string;
    readonly success: (name: string) => string;
  };
  readonly unblockModal: {
    readonly title: (name: string) => string;
    readonly description: string;
    readonly confirmLabel: string;
    readonly partialSuccess: (name: string, steps: string) => string;
    readonly success: (name: string) => string;
  };
  readonly assignPlanModal: {
    readonly title: (name: string) => string;
    readonly description: string;
    readonly tierLabel: string;
    readonly intervalLabel: string;
    readonly intervalMonthly: string;
    readonly intervalYearly: string;
    readonly lookupKeyLabel: string;
    readonly displayNameLabel: string;
    readonly priceLabel: string;
    readonly capabilitiesLabel: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: string;
  };
  readonly createModal: {
    readonly title: string;
    readonly description: string;
    readonly nameLabel: string;
    readonly namePlaceholder: string;
    readonly slugLabel: string;
    readonly slugPlaceholder: string;
    readonly slugHelp: string;
    readonly slugChecking: string;
    readonly slugAvailable: string;
    readonly slugTaken: string;
    readonly logoLabel: string;
    readonly logoPreviewAlt: string;
    readonly logoHelp: string;
    readonly logoInvalidFormat: string;
    readonly logoTooLarge: string;
    readonly logoReadError: string;
    readonly ownerLabel: string;
    readonly ownerPlaceholder: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: (name: string) => string;
  };
  readonly errors: Readonly<Record<CompanyErrorKey, string>>;
}

export const COMPANIES_MODALS_CONTENT: Bilingual<CompaniesModalsContent> = {
  'pt-BR': {
    reasonDialog: {
      reasonLabel: 'Motivo (mín. 20 caracteres)',
      reasonPlaceholder: 'Descreva o motivo...',
      characterCount: (n) => `${n} caracteres`,
      cancel: 'Cancelar',
    },
    blockModal: {
      title: (name) => `Bloquear ${name}?`,
      description:
        'Encerra as sessões dos membros e pausa a cobrança. Pode ser revertido em até 24 horas.',
      confirmLabel: 'Bloquear empresa',
      alreadyBlocked: (name) => `${name} já estava bloqueada.`,
      partialSuccess: (name, steps) => `${name} bloqueada com falhas: ${steps}.`,
      success: (name) => `${name} bloqueada. Sessões encerradas e cobrança pausada.`,
    },
    unblockModal: {
      title: (name) => `Desbloquear ${name}?`,
      description: 'Reativa a empresa e a cobrança. As pessoas precisarão entrar novamente.',
      confirmLabel: 'Desbloquear empresa',
      partialSuccess: (name, steps) => `${name} desbloqueada com falhas: ${steps}.`,
      success: (name) => `${name} desbloqueada. Membros precisam refazer login.`,
    },
    assignPlanModal: {
      title: (name) => `Atribuir plano — ${name}`,
      description:
        'Define o plano custom da organização. Planos cobrados usam Stripe; base e excedente ' +
        'zerados criam uma cortesia que o owner ativa no app sem cartão.',
      tierLabel: 'Tier',
      intervalLabel: 'Intervalo',
      intervalMonthly: 'Mensal',
      intervalYearly: 'Anual',
      lookupKeyLabel: 'Lookup key',
      displayNameLabel: 'Nome de exibição',
      priceLabel: 'Preço (USD)',
      capabilitiesLabel: 'Capabilities',
      cancel: 'Cancelar',
      submit: 'Atribuir plano',
      success: 'Plano da organização atribuído.',
    },
    createModal: {
      title: 'Nova organização',
      description:
        'Cria a org e nomeia o owner inicial (um usuário existente). O owner conclui a ' +
        'assinatura enterprise no app. Organizações não são mais criadas pelos próprios usuários.',
      nameLabel: 'Nome',
      namePlaceholder: 'Acme Corp',
      slugLabel: 'Namespace do publisher (@slug)',
      slugPlaceholder: 'acme',
      slugHelp:
        'Identidade pública dos packs da org (@slug). Único no Planuze inteiro — não pode ' +
        'colidir com outro publisher — e imutável após a criação.',
      slugChecking: 'Verificando disponibilidade…',
      slugAvailable: 'Namespace disponível.',
      slugTaken: 'Namespace já usado. Escolha outro.',
      logoLabel: 'Logo (opcional)',
      logoPreviewAlt: 'Prévia da logo',
      logoHelp: 'PNG, JPEG ou WebP até 512 KB. Aparece como identidade da org no app.',
      logoInvalidFormat: 'Formato inválido. Use PNG, JPEG ou WebP.',
      logoTooLarge: 'Imagem muito grande. Máximo 512 KB.',
      logoReadError: 'Não foi possível ler a imagem.',
      ownerLabel: 'Owner inicial',
      ownerPlaceholder: 'Selecionar owner...',
      cancel: 'Cancelar',
      submit: 'Criar organização',
      success: (name) => `Organização "${name}" criada.`,
    },
    errors: {
      notFound: 'Empresa não encontrada.',
      notBlocked: 'Empresa não está bloqueada.',
      ownerNotFound: 'Usuário owner não encontrado. Selecione um usuário existente.',
      invalidFormat: 'Domínio inválido. Use o formato acme.com (sem http, sem www).',
      publicBlocked: 'Domínio público (gmail etc.) não é aceito. Use o domínio corporativo.',
      slugTaken: 'Namespace do publisher já está em uso. Escolha outro @slug.',
      invalidLogo: 'Imagem inválida. Use PNG, JPEG ou WebP.',
      logoTooLarge: 'Logo muito grande. Use uma imagem de até 512 KB.',
      invalidSlug: 'Namespace inválido. Use letras minúsculas, números e hífen (3–39).',
      unauthorized: 'Você não tem permissão para esta ação.',
      planKeyTaken: 'A lookup key já está em uso. Escolha outra para o plano da organização.',
      graceExpired: 'Janela de 24h expirada. Emita a licença manualmente em Licenças.',
      network: 'Falha de conexão. Tente novamente.',
      default: 'Operação falhou. Tente novamente.',
      unexpected: 'Erro inesperado.',
    },
  },
  'en-US': {
    reasonDialog: {
      reasonLabel: 'Reason (min. 20 characters)',
      reasonPlaceholder: 'Describe the reason...',
      characterCount: (n) => `${n} characters`,
      cancel: 'Cancel',
    },
    blockModal: {
      title: (name) => `Block ${name}?`,
      description: 'Ends member sessions and pauses billing. This can be reversed within 24 hours.',
      confirmLabel: 'Block company',
      alreadyBlocked: (name) => `${name} was already blocked.`,
      partialSuccess: (name, steps) => `${name} blocked with failures: ${steps}.`,
      success: (name) => `${name} blocked. Sessions ended and billing paused.`,
    },
    unblockModal: {
      title: (name) => `Unblock ${name}?`,
      description: 'Reactivates the company and billing. Members will need to sign in again.',
      confirmLabel: 'Unblock company',
      partialSuccess: (name, steps) => `${name} unblocked with failures: ${steps}.`,
      success: (name) => `${name} unblocked. Members need to sign in again.`,
    },
    assignPlanModal: {
      title: (name) => `Assign plan — ${name}`,
      description:
        "Sets the organization's custom plan. Paid plans use Stripe; a zero base and overage " +
        'create a complimentary plan the owner activates in the app without a card.',
      tierLabel: 'Tier',
      intervalLabel: 'Interval',
      intervalMonthly: 'Monthly',
      intervalYearly: 'Yearly',
      lookupKeyLabel: 'Lookup key',
      displayNameLabel: 'Display name',
      priceLabel: 'Price (USD)',
      capabilitiesLabel: 'Capabilities',
      cancel: 'Cancel',
      submit: 'Assign plan',
      success: 'Organization plan assigned.',
    },
    createModal: {
      title: 'New organization',
      description:
        'Creates the org and names the initial owner (an existing user). The owner completes ' +
        'the enterprise subscription in the app. Organizations are no longer self-created by users.',
      nameLabel: 'Name',
      namePlaceholder: 'Acme Corp',
      slugLabel: 'Publisher namespace (@slug)',
      slugPlaceholder: 'acme',
      slugHelp:
        "Public identity for the org's packs (@slug). Unique across all of Planuze — cannot " +
        'collide with another publisher — and immutable after creation.',
      slugChecking: 'Checking availability…',
      slugAvailable: 'Namespace available.',
      slugTaken: 'Namespace already taken. Choose another.',
      logoLabel: 'Logo (optional)',
      logoPreviewAlt: 'Logo preview',
      logoHelp: "PNG, JPEG or WebP up to 512 KB. Appears as the org's identity in the app.",
      logoInvalidFormat: 'Invalid format. Use PNG, JPEG or WebP.',
      logoTooLarge: 'Image too large. Maximum 512 KB.',
      logoReadError: 'Could not read the image.',
      ownerLabel: 'Initial owner',
      ownerPlaceholder: 'Select owner...',
      cancel: 'Cancel',
      submit: 'Create organization',
      success: (name) => `Organization "${name}" created.`,
    },
    errors: {
      notFound: 'Company not found.',
      notBlocked: 'Company is not blocked.',
      ownerNotFound: 'Owner user not found. Select an existing user.',
      invalidFormat: 'Invalid domain. Use the format acme.com (no http, no www).',
      publicBlocked: 'Public domains (gmail etc.) are not accepted. Use the corporate domain.',
      slugTaken: 'Publisher namespace already in use. Choose another @slug.',
      invalidLogo: 'Invalid image. Use PNG, JPEG or WebP.',
      logoTooLarge: 'Logo too large. Use an image up to 512 KB.',
      invalidSlug: 'Invalid namespace. Use lowercase letters, numbers and hyphens (3–39).',
      unauthorized: 'You do not have permission for this action.',
      planKeyTaken: 'The lookup key is already in use. Choose another for the organization plan.',
      graceExpired: '24h window expired. Issue the license manually in Licenses.',
      network: 'Connection failed. Try again.',
      default: 'Operation failed. Try again.',
      unexpected: 'Unexpected error.',
    },
  },
};
